/**
 * Memory tracker — lifecycle counters + on-screen overlay + console logging.
 *
 * Activated only when URL contains `?mem-debug=1`. No-op otherwise (all exports
 * become cheap guarded calls). Goal: diagnose OOM on iPhone Safari when user
 * repeatedly opens/closes inside GLB scenes.
 *
 * Usage:
 *   incrementWebGLContext('glb' | 'splat')
 *   decrementWebGLContext('glb' | 'splat')
 *   incrementScene(kind)
 *   decrementScene(kind)
 *   registerMixer() / unregisterMixer()
 *   registerPoiManager() / unregisterPoiManager()
 *   registerPreviewModel() / unregisterPreviewModel()
 *   logEvent(name, data?)
 *   snapshot() → reads perf.memory + counters
 */

type SceneKind = 'glb' | 'splat' | 'globalMap' | 'main';

type PerformanceWithMemory = Performance & {
  memory?: {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
  };
};

interface Counters {
  activeWebGLContexts: number;
  activeGlbScenes: number;
  activeSplatScenes: number;
  activePoiManagers: number;
  activeMixers: number;
  activePreviewModels: number;
  totalRenderersCreated: number;
  totalScenesDisposed: number;
  poiArtifactOpenCount: number;
  poiArtifactCloseCount: number;
  webglContextLostCount: number;
  webglContextRestoredCount: number;
}

export interface MemorySnapshot extends Counters {
  usedJSHeapMb: number | null;
  totalJSHeapMb: number | null;
  jsHeapLimitMb: number | null;
  timestampMs: number;
}

const isBrowser = typeof window !== 'undefined';
const enabled = isBrowser && new URLSearchParams(window.location.search).get('mem-debug') === '1';

const counters: Counters = {
  activeWebGLContexts: 0,
  activeGlbScenes: 0,
  activeSplatScenes: 0,
  activePoiManagers: 0,
  activeMixers: 0,
  activePreviewModels: 0,
  totalRenderersCreated: 0,
  totalScenesDisposed: 0,
  poiArtifactOpenCount: 0,
  poiArtifactCloseCount: 0,
  webglContextLostCount: 0,
  webglContextRestoredCount: 0,
};

let overlay: HTMLDivElement | null = null;
let overlayRafId: number | null = null;
let overlayDirty = true;
let snapshotIntervalId: ReturnType<typeof setInterval> | null = null;

const LOG_PREFIX = '[MemoryTracker]';

const getMemoryMb = () => {
  const perf = (isBrowser ? performance : undefined) as PerformanceWithMemory | undefined;
  const mem = perf?.memory;
  if (!mem) {
    return { usedJSHeapMb: null, totalJSHeapMb: null, jsHeapLimitMb: null };
  }
  return {
    usedJSHeapMb: +(mem.usedJSHeapSize / (1024 * 1024)).toFixed(1),
    totalJSHeapMb: +(mem.totalJSHeapSize / (1024 * 1024)).toFixed(1),
    jsHeapLimitMb: +(mem.jsHeapSizeLimit / (1024 * 1024)).toFixed(1),
  };
};

export const snapshot = (): MemorySnapshot => ({
  ...counters,
  ...getMemoryMb(),
  timestampMs: isBrowser ? performance.now() : 0,
});

const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export const logEvent = (name: string, data?: unknown): void => {
  if (!enabled) return;
  const snap = snapshot();
  const payload =
    data !== undefined && data !== null && typeof data === 'object'
      ? { ...(data as Record<string, unknown>), ...snap }
      : data !== undefined
      ? { data, ...snap }
      : snap;
  console.log(`${LOG_PREFIX} ${name} ${safeStringify(payload)}`);
  markOverlayDirty();
};

const markOverlayDirty = (): void => {
  if (!enabled || !overlay) return;
  overlayDirty = true;
  if (overlayRafId !== null) return;
  overlayRafId = requestAnimationFrame(renderOverlay);
};

const renderOverlay = (): void => {
  overlayRafId = null;
  if (!enabled || !overlay || !overlayDirty) return;
  overlayDirty = false;
  const snap = snapshot();
  const lines: string[] = [
    `WebGL: ${snap.activeWebGLContexts}  (lost:${snap.webglContextLostCount} restored:${snap.webglContextRestoredCount})`,
    `Scenes: GLB ${snap.activeGlbScenes}  Splat ${snap.activeSplatScenes}`,
    `PoiMgrs: ${snap.activePoiManagers}`,
    `Mixers: ${snap.activeMixers}`,
    `PreviewModels: ${snap.activePreviewModels}`,
    snap.usedJSHeapMb !== null
      ? `Heap: ${snap.usedJSHeapMb} / ${snap.totalJSHeapMb} MB  (limit ${snap.jsHeapLimitMb})`
      : 'Heap: n/a',
    `Artifact: open ${snap.poiArtifactOpenCount} / close ${snap.poiArtifactCloseCount}`,
    `Cumulative: rend ${snap.totalRenderersCreated} disp ${snap.totalScenesDisposed}`,
  ];
  overlay.textContent = lines.join('\n');
};

const ensureOverlay = (): void => {
  if (!enabled || overlay || typeof document === 'undefined') return;
  overlay = document.createElement('div');
  Object.assign(overlay.style, {
    position: 'fixed',
    top: '8px',
    right: '8px',
    zIndex: '999999',
    background: 'rgba(0,0,0,0.75)',
    color: '#0f0',
    padding: '6px 10px',
    fontFamily: 'monospace',
    fontSize: '11px',
    lineHeight: '1.4',
    whiteSpace: 'pre',
    borderRadius: '6px',
    pointerEvents: 'none',
    maxWidth: '50vw',
  } as CSSStyleDeclaration);
  overlay.setAttribute('data-mem-debug', '1');
  document.body.appendChild(overlay);
  markOverlayDirty();
};

export const incrementWebGLContext = (kind: SceneKind): void => {
  if (!enabled) return;
  counters.activeWebGLContexts++;
  counters.totalRenderersCreated++;
  logEvent(`webgl context created (${kind})`);
};

export const decrementWebGLContext = (kind: SceneKind): void => {
  if (!enabled) return;
  counters.activeWebGLContexts = Math.max(0, counters.activeWebGLContexts - 1);
  logEvent(`webgl context released (${kind})`);
};

export const incrementScene = (kind: SceneKind): void => {
  if (!enabled) return;
  if (kind === 'glb') counters.activeGlbScenes++;
  else if (kind === 'splat') counters.activeSplatScenes++;
  // 'globalMap' — отдельная категория, не засчитывается в GLB/Splat счётчики
  markOverlayDirty();
};

export const decrementScene = (kind: SceneKind): void => {
  if (!enabled) return;
  if (kind === 'glb') counters.activeGlbScenes = Math.max(0, counters.activeGlbScenes - 1);
  else if (kind === 'splat') counters.activeSplatScenes = Math.max(0, counters.activeSplatScenes - 1);
  counters.totalScenesDisposed++;
  markOverlayDirty();
};

export const registerMixer = (): void => {
  if (!enabled) return;
  counters.activeMixers++;
  markOverlayDirty();
};

export const unregisterMixer = (): void => {
  if (!enabled) return;
  counters.activeMixers = Math.max(0, counters.activeMixers - 1);
  markOverlayDirty();
};

export const registerPoiManager = (): void => {
  if (!enabled) return;
  counters.activePoiManagers++;
  markOverlayDirty();
};

export const unregisterPoiManager = (): void => {
  if (!enabled) return;
  counters.activePoiManagers = Math.max(0, counters.activePoiManagers - 1);
  markOverlayDirty();
};

export const registerPreviewModel = (): void => {
  if (!enabled) return;
  counters.activePreviewModels++;
  markOverlayDirty();
};

export const unregisterPreviewModel = (): void => {
  if (!enabled) return;
  counters.activePreviewModels = Math.max(0, counters.activePreviewModels - 1);
  markOverlayDirty();
};

export const recordPoiArtifactOpen = (): void => {
  if (!enabled) return;
  counters.poiArtifactOpenCount++;
  logEvent('poi artifact opened');
};

export const recordPoiArtifactClose = (): void => {
  if (!enabled) return;
  counters.poiArtifactCloseCount++;
  logEvent('poi artifact closed');
};

export const recordWebglContextLost = (): void => {
  if (!enabled) return;
  counters.webglContextLostCount++;
  logEvent('webglcontextlost event');
};

export const recordWebglContextRestored = (): void => {
  if (!enabled) return;
  counters.webglContextRestoredCount++;
  logEvent('webglcontextrestored event');
};

export const isMemoryDebugEnabled = (): boolean => enabled;

// ─────────────────────────────────────────────────────────────────────
// Buffer-size diagnostics (iOS OOM hunt — JS heap is invisible on Safari)
// ─────────────────────────────────────────────────────────────────────

/**
 * Log a typed-array / ArrayBuffer size with a label, formatted as MB.
 *
 * iOS Safari does not expose `performance.memory`, so we cannot read the JS
 * heap directly. The next-best diagnostic is to log every BIG buffer the app
 * knows about (HDR EXR skybox `Float32Array`, splat `fileBytes` `Uint8Array`,
 * Spark `PackedSplats.source.data` `Uint8Array`, GLB `ArrayBuffer`). Sum of
 * these per-session, compared between transitions, identifies what's leaking.
 *
 * Pass `byteLength` (works for `ArrayBuffer`, `TypedArray.byteLength`,
 * `Blob.size`). For unknown sources, pass 0 / null and we'll log "n/a".
 */
export const logBigBuffer = (
  label: string,
  byteLength: number | null | undefined,
  meta?: Record<string, unknown>,
): void => {
  if (!enabled) return;
  const mb = typeof byteLength === 'number' && byteLength > 0
    ? +(byteLength / (1024 * 1024)).toFixed(2)
    : null;
  logEvent(`big buffer: ${label}`, {
    bytes: byteLength ?? null,
    mb,
    ...(meta ?? {}),
  });
};

// ─────────────────────────────────────────────────────────────────────
// WeakRef post-dispose retention probe
// ─────────────────────────────────────────────────────────────────────

/**
 * After a scene tear-down, schedule WeakRef checks at progressively longer
 * delays. If `deref()` returns the object after several seconds, that object
 * is being held by a closure / event listener / Effector store / module-level
 * variable and is leaking. iOS JSC is slow to GC, so we deliberately probe
 * up to ~10 seconds out before declaring a leak.
 *
 * NB: WeakRef availability — Safari 14.1+ / iOS 14.5+. Older browsers fall
 * back to a no-op (we can't track without it).
 *
 * Usage in `dispose`:
 *   trackPostDispose('splatMesh', splatMeshForDiagnostics);
 *   trackPostDispose('sparkRenderer', sparkRenderer);
 *   trackPostDispose('scene', scene);
 *
 * Each call schedules `setTimeout(..., delayMs)` for each entry in
 * `RETENTION_PROBE_DELAYS_MS`. The setTimeout closure itself captures only the
 * WeakRef — NOT a strong ref to the target — so the probe never blocks GC.
 */
const RETENTION_PROBE_DELAYS_MS = [1500, 4000, 9000];

/**
 * Reflective inspection of an object's own enumerable + non-enumerable
 * properties. Returns a compact summary of which fields are non-null and
 * what their constructor names are. Used inside `trackPostDispose` at the
 * final probe point — if a target is STILL alive after 9s, we want to know
 * WHICH of its fields are pinning it down (most often: a strong ref to a
 * THREE.WebGLRenderer / Scene / Camera / Material / Worker that some
 * external listener is holding).
 *
 * Limits per object:
 *   - First 80 own property names
 *   - Skip primitives (numbers, strings, booleans) — only objects/arrays/funcs
 *   - For each non-null object value: log its constructor name + (if function)
 *     its name + (if array) its length
 *   - Catches per-property access errors (some fields are getters that throw
 *     once disposed)
 */
const summarizeRetainedFields = (target: object): Array<{ key: string; type: string; extra?: string }> => {
  const out: Array<{ key: string; type: string; extra?: string }> = [];
  let names: string[];
  try {
    names = Object.getOwnPropertyNames(target);
  } catch {
    return out;
  }
  const limit = Math.min(names.length, 80);
  for (let i = 0; i < limit; i++) {
    const key = names[i];
    let value: unknown;
    try {
      value = (target as Record<string, unknown>)[key];
    } catch {
      out.push({ key, type: 'getter-threw' });
      continue;
    }
    if (value === null || value === undefined) continue;
    const t = typeof value;
    if (t !== 'object' && t !== 'function') continue;
    const ctorName = (value as { constructor?: { name?: string } })?.constructor?.name ?? t;
    let extra: string | undefined;
    if (Array.isArray(value)) {
      extra = `len=${value.length}`;
    } else if (t === 'function') {
      extra = `fn:${(value as { name?: string }).name ?? '<anon>'}`;
    } else if ((value as { isTexture?: boolean })?.isTexture) {
      const data = ((value as { source?: { data?: { byteLength?: number } } }).source?.data);
      if (data && typeof data.byteLength === 'number') {
        extra = `${(data.byteLength / (1024 * 1024)).toFixed(1)}MB`;
      }
    } else if ((value as { isObject3D?: boolean })?.isObject3D) {
      const children = (value as { children?: unknown[] }).children;
      extra = `obj3D children=${Array.isArray(children) ? children.length : '?'}`;
    }
    out.push({ key, type: ctorName, extra });
  }
  return out;
};

export const trackPostDispose = (
  label: string,
  target: object | null | undefined,
  meta?: Record<string, unknown>,
): void => {
  if (!enabled || !target) return;
  if (typeof WeakRef === 'undefined') {
    // Older browser — log a one-time note instead of probing.
    logEvent(`retention probe unavailable for ${label}`, { ...(meta ?? {}) });
    return;
  }
  const ref = new WeakRef(target);
  const startedAt = isBrowser ? performance.now() : 0;
  for (const delay of RETENTION_PROBE_DELAYS_MS) {
    setTimeout(() => {
      const stillAlive = ref.deref();
      const ageMs = isBrowser ? +(performance.now() - startedAt).toFixed(0) : delay;
      const isLastProbe = delay === RETENTION_PROBE_DELAYS_MS[RETENTION_PROBE_DELAYS_MS.length - 1];
      logEvent(`retention probe: ${label}`, {
        ageMs,
        stillAlive: stillAlive !== undefined,
        // Light-touch identification of the held object (constructor name only,
        // no deep traversal — must stay GC-neutral).
        type: stillAlive
          ? ((stillAlive as { constructor?: { name?: string } }).constructor?.name ?? 'unknown')
          : null,
        // On the FINAL probe of a still-alive target, dump its non-null field
        // summary — narrows down which property (and which type) is pinning
        // the target. We hold the ref only briefly through this synchronous
        // function; the WeakRef itself does NOT pin the target.
        retainedFields:
          stillAlive && isLastProbe ? summarizeRetainedFields(stillAlive) : undefined,
        ...(meta ?? {}),
      });
    }, delay);
  }
};

// ─────────────────────────────────────────────────────────────────────
// Three.js / DOM diagnostics (iOS OOM investigation)
// ─────────────────────────────────────────────────────────────────────

// Minimal type surface to avoid a hard dep on 'three' in the debug module.
interface ThreeLikeWebGLProgram {
  id?: number;
  name?: string;
  cacheKey?: string;
  usedTimes?: number;
}
interface ThreeLikeRenderer {
  info?: {
    memory?: { geometries?: number; textures?: number };
    // Three.js types `programs` as `WebGLProgram[] | null`; accept that.
    programs?: ArrayLike<ThreeLikeWebGLProgram> | null;
    render?: { calls?: number; triangles?: number; frame?: number };
  };
  domElement?: HTMLCanvasElement;
}
interface ThreeLikeObject3D {
  type?: string;
  children?: ThreeLikeObject3D[];
  geometry?: unknown;
  material?: unknown;
  traverse?: (cb: (o: ThreeLikeObject3D) => void) => void;
  // Scene-specific fields (Scene extends Object3D). Present as null on non-Scene objects.
  background?: unknown;
  environment?: unknown;
}
interface ThreeLikeTexture {
  name?: string;
  isTexture?: boolean;
  isCubeTexture?: boolean;
  image?: { width?: number; height?: number } | HTMLImageElement | null;
  source?: { data?: unknown } | null;
  format?: number;
  type?: number;
  constructor?: { name?: string };
}

// --- Spark internals probing (read-only, purely diagnostic) ---
// These shapes mirror the private state of `@sparkjsdev/spark` observed in
// `spark.module.js` (SparkRenderer / SplatAccumulator / PackedSplats / SplatMesh).
// All access is wrapped in try/catch — if a field is missing or renamed in a
// future spark version, the probe degrades to null instead of throwing.
type TypedArrayLike = { byteLength?: number; length?: number } | null | undefined;

interface SparkAccumulatorLike {
  mapping?: ArrayLike<unknown>;
  splats?: {
    packedArray?: TypedArrayLike;
    maxSplats?: number;
    numSplats?: number;
    // PackedSplats.source: THREE.DataArrayTexture (uploaded splat-data atlas).
    // `source.data` (Uint8Array) is the CPU-side copy of the GPU upload —
    // NOT released by `texture.dispose()`. Sized roughly numSplats * 32 bytes
    // = ~50–100 MB for a 1.5–3M-splat scene. PRIMARY iOS OOM suspect after the
    // packedArray fix.
    source?: {
      data?: TypedArrayLike;
      // Three.js source.data for compressed textures wraps a different shape;
      // we only care about the byteLength
    } | null;
    // PackedSplats.target: WebGLArrayRenderTarget (no CPU-side data, GPU only).
    // No probe field needed.
  };
}

interface SparkRendererLike {
  active?: SparkAccumulatorLike;
  freeAccumulators?: SparkAccumulatorLike[];
  autoViewpoints?: ArrayLike<unknown>;
  accumulatorCount?: number;
  pendingUpdate?: { scene?: unknown; timeoutId?: number };
}

interface SplatMeshLike {
  packedSplats?: {
    packedArray?: TypedArrayLike;
    maxSplats?: number;
    numSplats?: number;
    // `constructor` here is the PackedSplats class itself; `generatorProgram`
    // is a class-level static Map that accumulates compiled DynoPrograms
    // across ALL PackedSplats instances in the session. Central suspect for
    // cross-scene leak (Класс 2.1).
    constructor?: { generatorProgram?: { size?: number } };
  };
}

/**
 * Extract size of spark's static generator-program cache. The cache is shared
 * across all PackedSplats instances in the app session (class-level static
 * Map); we access it via any available PackedSplats instance's `.constructor`.
 * Returns `null` if neither a sparkRenderer nor a splatMesh is provided, or if
 * the field is unreachable.
 */
const readGeneratorProgramMapSize = (
  sparkRenderer: SparkRendererLike | null | undefined,
  splatMesh: SplatMeshLike | null | undefined,
): number | null => {
  try {
    const pkInstance =
      splatMesh?.packedSplats ??
      sparkRenderer?.active?.splats ??
      sparkRenderer?.freeAccumulators?.[0]?.splats;
    const ctor = (pkInstance as { constructor?: { generatorProgram?: { size?: number } } } | undefined)?.constructor;
    const size = ctor?.generatorProgram?.size;
    return typeof size === 'number' ? size : null;
  } catch {
    return null;
  }
};

const packedArrayBytes = (arr: TypedArrayLike): number => {
  if (!arr) return 0;
  return typeof arr.byteLength === 'number' ? arr.byteLength : 0;
};

/**
 * Snapshot of Three.js renderer state + DOM asset counts. Safe to call at any
 * point in a scene's lifecycle; if anything is missing (renderer disposed,
 * scene cleared) the affected fields become `null`/`0` instead of throwing.
 *
 * Fields:
 *   gpuGeometries / gpuTextures — alive on the GL context per `renderer.info.memory`
 *   shaderPrograms               — compiled WebGL programs in the renderer cache
 *   sceneObjects / sceneMeshes   — traverse-count of the given scene graph
 *   sceneMaterials / sceneTex    — unique materials / textures in scene (Set dedup)
 *   domCanvases / domImages      — document-level <canvas> and <img> counts
 *   liveBlobUrls                 — approximate live Blob URLs (best-effort, may be 0)
 */
/**
 * Measure a dispose step: snapshot renderer.info.memory before and after the
 * provided callback, log the delta. Used to identify which specific sub-dispose
 * in a scene teardown fails to release its GPU resources.
 *
 * Usage:
 *   measureDisposeStep('splatMesh.dispose', renderer, () => splatMesh.dispose());
 *
 * The delta in a well-behaved step is NEGATIVE (resources freed) or zero.
 * POSITIVE means the step somehow created more resources (shouldn't happen in
 * dispose). ZERO when there's a leak — the step should have freed something
 * but didn't.
 */
export const measureDisposeStep = (
  label: string,
  renderer: ThreeLikeRenderer | null | undefined,
  fn: () => void,
): void => {
  if (!enabled || !renderer?.info) {
    fn();
    return;
  }
  const beforeGeo = renderer.info.memory?.geometries ?? 0;
  const beforeTex = renderer.info.memory?.textures ?? 0;
  const beforeProg = renderer.info.programs?.length ?? 0;
  fn();
  const afterGeo = renderer.info.memory?.geometries ?? 0;
  const afterTex = renderer.info.memory?.textures ?? 0;
  const afterProg = renderer.info.programs?.length ?? 0;
  logEvent(`dispose-step: ${label}`, {
    geoDelta: afterGeo - beforeGeo,
    texDelta: afterTex - beforeTex,
    progDelta: afterProg - beforeProg,
    geoAfter: afterGeo,
    texAfter: afterTex,
    progAfter: afterProg,
  });
};

export const logThreeJsDiagnostics = (
  phase: string,
  opts?: {
    renderer?: ThreeLikeRenderer | null;
    scene?: ThreeLikeObject3D | null;
    sceneId?: string;
    /**
     * `@sparkjsdev/spark` SparkRenderer instance. When provided, extracts
     * private-field counters (active/free accumulators, mapping lengths,
     * pendingUpdate state, generatorProgram static map size) via reflective
     * access. Typed as `unknown` to avoid pulling spark's private types into
     * this module; call sites pass the instance as-is.
     */
    sparkRenderer?: unknown;
    /**
     * `SplatMesh` from spark. When provided, reports main-thread packedArray
     * byte size and splat counts. Critical for tracking Класс 1.1 leaks
     * (packedArray not nulled in spark's `PackedSplats.dispose`).
     * Typed as `unknown` for the same reason as `sparkRenderer`.
     */
    splatMesh?: unknown;
  },
): void => {
  if (!enabled) return;
  const { renderer, scene, sceneId } = opts ?? {};
  const sparkRenderer = opts?.sparkRenderer as SparkRendererLike | null | undefined;
  const splatMesh = opts?.splatMesh as SplatMeshLike | null | undefined;

  // Renderer.info stats
  let gpuGeometries: number | null = null;
  let gpuTextures: number | null = null;
  let shaderPrograms: number | null = null;
  let renderCalls: number | null = null;
  let renderTriangles: number | null = null;
  if (renderer?.info) {
    gpuGeometries = renderer.info.memory?.geometries ?? null;
    gpuTextures = renderer.info.memory?.textures ?? null;
    shaderPrograms = renderer.info.programs?.length ?? 0;
    renderCalls = renderer.info.render?.calls ?? null;
    renderTriangles = renderer.info.render?.triangles ?? null;
  }

  // --- Spark private-internals probes (best-effort, null when unreachable) ---
  let sparkActiveMappingLength: number | null = null;
  let sparkAutoViewpointsLength: number | null = null;
  let sparkAccumulatorCount: number | null = null;
  let sparkFreeAccumulatorsLength: number | null = null;
  let sparkFreeMappingLengthTotal: number | null = null;
  let sparkActivePackedArrayBytes: number | null = null;
  let sparkActiveMaxSplats: number | null = null;
  let sparkActiveNumSplats: number | null = null;
  let sparkFreePackedArrayBytesTotal: number | null = null;
  // CPU-side splat atlas (DataArrayTexture.source.data) — primary iOS OOM
  // suspect after the packedArray fix. Sum across active + free accumulators.
  let sparkActiveSourceBytes: number | null = null;
  let sparkFreeSourceBytesTotal: number | null = null;
  let sparkPendingUpdateHasScene: boolean | null = null;
  let sparkPendingUpdateTimeoutActive: boolean | null = null;
  if (sparkRenderer) {
    try {
      sparkActiveMappingLength = sparkRenderer.active?.mapping?.length ?? null;
      sparkAutoViewpointsLength = sparkRenderer.autoViewpoints?.length ?? null;
      sparkAccumulatorCount = sparkRenderer.accumulatorCount ?? null;
      sparkFreeAccumulatorsLength = sparkRenderer.freeAccumulators?.length ?? null;
      sparkActivePackedArrayBytes = packedArrayBytes(sparkRenderer.active?.splats?.packedArray);
      sparkActiveMaxSplats = sparkRenderer.active?.splats?.maxSplats ?? null;
      sparkActiveNumSplats = sparkRenderer.active?.splats?.numSplats ?? null;
      sparkActiveSourceBytes = packedArrayBytes(sparkRenderer.active?.splats?.source?.data);
      if (Array.isArray(sparkRenderer.freeAccumulators)) {
        let freeMappingTotal = 0;
        let freeBytesTotal = 0;
        let freeSourceBytesTotal = 0;
        for (const acc of sparkRenderer.freeAccumulators) {
          freeMappingTotal += acc?.mapping?.length ?? 0;
          freeBytesTotal += packedArrayBytes(acc?.splats?.packedArray);
          freeSourceBytesTotal += packedArrayBytes(acc?.splats?.source?.data);
        }
        sparkFreeMappingLengthTotal = freeMappingTotal;
        sparkFreePackedArrayBytesTotal = freeBytesTotal;
        sparkFreeSourceBytesTotal = freeSourceBytesTotal;
      }
      sparkPendingUpdateHasScene = sparkRenderer.pendingUpdate?.scene != null;
      sparkPendingUpdateTimeoutActive =
        typeof sparkRenderer.pendingUpdate?.timeoutId === 'number' &&
        sparkRenderer.pendingUpdate.timeoutId !== -1;
    } catch {
      /* spark internals shape mismatch — leave as null */
    }
  }

  // SplatMesh main-thread packedArray — decoded splat data (24-96 MB typical)
  let splatMeshPackedArrayBytes: number | null = null;
  let splatMeshMaxSplats: number | null = null;
  let splatMeshNumSplats: number | null = null;
  if (splatMesh) {
    try {
      splatMeshPackedArrayBytes = packedArrayBytes(splatMesh.packedSplats?.packedArray);
      splatMeshMaxSplats = splatMesh.packedSplats?.maxSplats ?? null;
      splatMeshNumSplats = splatMesh.packedSplats?.numSplats ?? null;
    } catch {
      /* splatMesh disposed / shape mismatch */
    }
  }

  // Static class-level Map size — the cumulative-across-session suspect.
  const generatorProgramMapSize = readGeneratorProgramMapSize(sparkRenderer, splatMesh);

  // Scene graph stats via traverse
  let sceneObjects = 0;
  let sceneMeshes = 0;
  const uniqueMaterials = new Set<unknown>();
  const uniqueGeometries = new Set<unknown>();
  const uniqueTextures = new Set<unknown>();
  // Breakdown: descriptive strings like "envMap: PMREM(CubeTexture 256x256)"
  // Helps identify WHICH textures are heavyweight (PMREM, HDR skybox, POI icons)
  const textureBreakdown: string[] = [];
  const materialBreakdown: string[] = [];
  const describeTexture = (tex: ThreeLikeTexture, fieldKey?: string): string => {
    const ctorName = tex.constructor?.name ?? 'Texture';
    const name = typeof tex.name === 'string' && tex.name.length > 0 ? tex.name : fieldKey ?? '';
    let dim = '?';
    const img = tex.image as { width?: number; height?: number } | null | undefined;
    if (img && typeof img.width === 'number' && typeof img.height === 'number') {
      dim = `${img.width}x${img.height}`;
    }
    // CPU-side data attached via `source.data` — for HDR EXR (Float32Array),
    // PNG/JPG ImageBitmap, raw Uint8Array. Critical for iOS heap accounting:
    // texture.dispose() releases GPU but NOT this CPU buffer until JS GC.
    const data = (tex.source?.data as { byteLength?: number; length?: number } | null | undefined);
    let bytesTag = '';
    if (data && typeof data.byteLength === 'number') {
      const mb = (data.byteLength / (1024 * 1024)).toFixed(1);
      bytesTag = ` ${mb}MB`;
    } else if (data && typeof data.length === 'number') {
      bytesTag = ` len=${data.length}`;
    }
    const tag = name ? `${ctorName}<${name}>` : ctorName;
    return `${tag} ${dim}${bytesTag}`;
  };
  if (scene && typeof scene.traverse === 'function') {
    try {
      scene.traverse((obj) => {
        sceneObjects++;
        if (obj.type === 'Mesh' || obj.type === 'SkinnedMesh' || obj.type === 'Points' || obj.type === 'LineSegments') {
          sceneMeshes++;
        }
        if (obj.geometry) uniqueGeometries.add(obj.geometry);
        const mat = obj.material;
        if (mat) {
          const mats = Array.isArray(mat) ? mat : [mat];
          for (const m of mats) {
            if (!m) continue;
            if (!uniqueMaterials.has(m)) {
              uniqueMaterials.add(m);
              const matRecord = m as { name?: string; constructor?: { name?: string } };
              const matCtor = matRecord.constructor?.name ?? 'Material';
              const matName = matRecord.name || '';
              materialBreakdown.push(matName ? `${matCtor}<${matName}>` : matCtor);
            }
            // Scan material for textures via key names (avoids THREE import)
            for (const key of Object.keys(m as Record<string, unknown>)) {
              const v = (m as Record<string, unknown>)[key];
              if (v && typeof v === 'object' && (v as { isTexture?: boolean }).isTexture) {
                if (!uniqueTextures.has(v)) {
                  uniqueTextures.add(v);
                  textureBreakdown.push(describeTexture(v as ThreeLikeTexture, key));
                }
              }
            }
          }
        }
      });
    } catch {
      /* scene disposed or cleared — skip */
    }
  }

  // Scene-level background/environment probe — Three.js Scene exposes these directly.
  // Non-null after dispose = leak (env should be nulled in EnvironmentManager.dispose).
  let sceneBackgroundType: string | null = null;
  let sceneEnvironmentType: string | null = null;
  if (scene) {
    const sceneLike = scene as { background?: unknown; environment?: unknown };
    const bg = sceneLike.background as { constructor?: { name?: string }; isTexture?: boolean; isColor?: boolean } | null | undefined;
    if (bg) {
      sceneBackgroundType = bg.constructor?.name ?? (bg.isTexture ? 'Texture?' : bg.isColor ? 'Color?' : 'unknown');
    }
    const env = sceneLike.environment as { constructor?: { name?: string }; isTexture?: boolean } | null | undefined;
    if (env) {
      sceneEnvironmentType = env.constructor?.name ?? (env.isTexture ? 'Texture?' : 'unknown');
    }
    // Also traverse texture on background (a Texture) / environment
    if (bg && (bg as { isTexture?: boolean }).isTexture && !uniqueTextures.has(bg)) {
      uniqueTextures.add(bg);
      textureBreakdown.push(`[scene.background] ${describeTexture(bg as ThreeLikeTexture)}`);
    }
    if (env && (env as { isTexture?: boolean }).isTexture && !uniqueTextures.has(env)) {
      uniqueTextures.add(env);
      textureBreakdown.push(`[scene.environment] ${describeTexture(env as ThreeLikeTexture)}`);
    }
  }

  // WebGLPrograms cache in shared renderer — accumulates across scenes if
  // materials holding them aren't disposed. `usedTimes` is Three.js internal
  // refcount; programs with usedTimes > 0 are still held by live materials.
  // cacheKey encodes shader-source + defines — identical materials share a program.
  const programBreakdown: Array<{ id?: number; name?: string; used?: number; key?: string }> = [];
  if (renderer?.info?.programs) {
    const programs = renderer.info.programs;
    const limit = Math.min(programs.length, 30);
    for (let i = 0; i < limit; i++) {
      const p = programs[i];
      if (!p) continue;
      const cacheKey = typeof p.cacheKey === 'string' ? p.cacheKey : '';
      // Shorten — full cacheKey can be 10KB (all defines + material params).
      // First 100 chars are usually enough to distinguish material types
      // (RawShaderMaterial vs MeshStandardMaterial, etc.)
      programBreakdown.push({
        id: p.id,
        name: p.name,
        used: p.usedTimes,
        key: cacheKey.slice(0, 100),
      });
    }
  }

  // DOM counts + sample of img sources grouped by parent for leak triangulation
  let domCanvases = 0;
  let domImages = 0;
  const imgGroupCounts: Record<string, number> = {};
  const imgSampleSrcs: string[] = [];
  if (typeof document !== 'undefined') {
    try { domCanvases = document.querySelectorAll('canvas').length; } catch {}
    try {
      const imgs = document.querySelectorAll('img');
      domImages = imgs.length;
      // Group by nearest meaningful ancestor (class or tag) to identify the
      // leak source. Limit sampling to avoid log explosion.
      const sampleLimit = Math.min(domImages, 60);
      for (let i = 0; i < sampleLimit; i++) {
        const img = imgs[i] as HTMLImageElement;
        // Find ancestor with a class or className we can identify.
        let ancestor: Element | null = img.parentElement;
        let key = 'orphan';
        let depth = 0;
        while (ancestor && depth < 6) {
          const cls = typeof ancestor.className === 'string' ? ancestor.className.trim().split(/\s+/)[0] : '';
          if (cls && cls.length < 60) {
            key = `${ancestor.tagName.toLowerCase()}.${cls}`;
            break;
          }
          if (ancestor.id) {
            key = `${ancestor.tagName.toLowerCase()}#${ancestor.id}`;
            break;
          }
          ancestor = ancestor.parentElement;
          depth++;
        }
        imgGroupCounts[key] = (imgGroupCounts[key] ?? 0) + 1;
        // Also collect first few src examples for each group
        if (imgSampleSrcs.length < 8) {
          const rawSrc = img.currentSrc || img.src || '';
          const shortSrc = rawSrc.length > 80 ? '…' + rawSrc.slice(-80) : rawSrc;
          imgSampleSrcs.push(`[${key}] ${shortSrc}`);
        }
      }
    } catch {}
  }

  logEvent(`threejs diagnostics: ${phase}`, {
    sceneId: sceneId ?? undefined,
    gpuGeometries,
    gpuTextures,
    shaderPrograms,
    renderCalls,
    renderTriangles,
    sceneObjects,
    sceneMeshes,
    sceneMaterials: uniqueMaterials.size,
    sceneGeometries: uniqueGeometries.size,
    sceneTextures: uniqueTextures.size,
    domCanvases,
    domImages,
    imgGroupCounts,
    imgSampleSrcs,
    // --- Spark / SplatMesh internals probes (null when refs not supplied) ---
    sparkActiveMappingLength,
    sparkAutoViewpointsLength,
    sparkAccumulatorCount,
    sparkFreeAccumulatorsLength,
    sparkFreeMappingLengthTotal,
    sparkActivePackedArrayBytes,
    sparkActiveMaxSplats,
    sparkActiveNumSplats,
    sparkFreePackedArrayBytesTotal,
    sparkActiveSourceBytes,
    sparkFreeSourceBytesTotal,
    sparkPendingUpdateHasScene,
    sparkPendingUpdateTimeoutActive,
    splatMeshPackedArrayBytes,
    splatMeshMaxSplats,
    splatMeshNumSplats,
    generatorProgramMapSize,
    // --- Residual leak hunt (Шаг A) ---
    // Shader-program breakdown — identifies which compiled WebGL programs
    // remain in the shared renderer after dispose (cross-scene residual).
    // `used` = Three.js refcount, `key` = first 100 chars of cacheKey (defines).
    programBreakdown,
    // Scene-graph texture breakdown — name + dimensions per unique texture.
    // Heavyweight ones (PMREM CubeTexture, 4K EXR, HDR panorama) stand out.
    textureBreakdown,
    // Materials in scene — tells whether the leak is in one class (PointsMaterial,
    // RawShaderMaterial) or spread across the scene graph.
    materialBreakdown,
    // Scene.background / scene.environment types — should be null post-dispose.
    // Non-null = EnvironmentManager / TellerAssistant left env/bg references.
    sceneBackgroundType,
    sceneEnvironmentType,
  });
};

const initPageLifecycleListeners = (): void => {
  if (!enabled || !isBrowser) return;
  window.addEventListener('pagehide', (event) => {
    // Last log before potential OOM reload. If page is killed, persisted=false.
    logEvent('pagehide', { persisted: event.persisted });
  });
  window.addEventListener('visibilitychange', () => {
    logEvent('visibilitychange', { hidden: document.hidden });
  });
};

const bootstrap = (): void => {
  if (!enabled || !isBrowser) return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureOverlay, { once: true });
  } else {
    ensureOverlay();
  }
  initPageLifecycleListeners();
  // Periodic snapshot so we have a time series even if no events fire.
  snapshotIntervalId = setInterval(() => {
    logEvent('periodic snapshot');
  }, 2000);
};

bootstrap();
